import React, { useEffect } from 'react';

import clsx from 'clsx';
import { observer } from 'mobx-react';

import { useStores } from 'hooks/use-stores';
import useDebouncedValue from 'hooks/useDebouncedValue';

interface DepositAmountInputProps {
    stringAmount: string;
    className?: string;
    autoFocus?: boolean;
    formatAmount: (amount: number, stringAmount: string) => void;
    onChange?: (amount: number) => void;
    hasError?: boolean;
}

const DepositAmountInput = ({
    stringAmount,
    className = undefined,
    autoFocus,
    formatAmount,
    onChange,
    hasError = false,
}: DepositAmountInputProps): JSX.Element => {
    const debouncedStringAmount = useDebouncedValue(stringAmount, 900);

    const { traderStore } = useStores();
    const bankStore = traderStore.bank;

    const formatStringValue = (value: string): string => {
        let numberString = value.replaceAll(',', '').replace('¢', '');

        if (numberString.length > 0 && numberString.charAt(0) === '$') {
            numberString = numberString.substr(1);
        }
        return numberString;
    };

    useEffect(
        () => {
            if (!bankStore.loadedLimit) {
                bankStore.loadDepositLimitInfo();
            }
        }, // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [bankStore.loadedLimit],
    );

    // On debounced string amount change - now only used for other purposes
    // Validation is handled immediately in onChangeCustomDepositInput
    useEffect(
        () => {
            if (debouncedStringAmount.length === 0) {
                return;
            }

            const numberString = formatStringValue(debouncedStringAmount);
            const numberValue = Number(numberString);

            if (numberValue === 0) {
                return;
            }
        },
        // TODO: check or fix hooks error
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [debouncedStringAmount],
    );

    const onChangeCustomDepositInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const numberString = formatStringValue(e.target.value);
        const numberValue = Number(numberString);

        if (numberString === '' || numberValue === 0) {
            formatAmount(0, '');
            if (onChange) {
                onChange(0);
            }
            return;
        }

        formatAmount(numberValue, numberString);
        // Immediately call onChange for instant validation
        if (onChange) {
            onChange(numberValue);
        }
    };

    // Calculate display value once per render for consistent input value and positioning
    const displayValue = formatStringValue(stringAmount);

    return (
        <div className={'flex items-start justify-center relative overflow-hidden border-none'}>
            <input
                type={'number'}
                value={displayValue}
                onChange={onChangeCustomDepositInput}
                placeholder={'0'}
                step={'0.01'}
                className={clsx(
                    className,
                    'text-[56px] -ml-8 bg-[#00000000] placeholder:text-fill-x40 font-semibold outline-none font-sans pl-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                    hasError ? 'border-2 border-red-500' : 'border-none',
                )}
                style={{
                    fontFamily: 'var(--inter-font)',
                    fontFeatureSettings: 'lining-nums tabular-nums',
                    textAlign: 'center',
                    minWidth: 'calc(100% - 32px)',
                }}
                autoFocus={autoFocus}
                onKeyDown={bankStore.onKeyDownCustomDepositInput}
            />
            <p
                className={
                    'text-[24px] text-text-x10 font-semibold absolute pointer-events-none transition-all duration-100 ease-out mt-1.5'
                }
                style={{
                    fontFamily: 'var(--inter-font)',
                    left: 'calc(50% - ' + Math.max(displayValue.length * 17 + 32, 48) + 'px)',
                }}
            >
                {'$'}
            </p>
        </div>
    );
};

export default observer(DepositAmountInput);
